import * as React from 'react'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { useNavigate, useRouter } from '@tanstack/react-router'
import { useAuth } from '@modules/auth/AuthProvider'
import { PortalBrand } from '@shared/Brand'
import { getLoginErrorMessage } from './loginErrors'
import { normalizeNextPath } from './nextPath'

export default function LoginPage() {
  const { login, user } = useAuth()
  const navigate = useNavigate()
  const router = useRouter()
  const [username, setUsername] = React.useState('')
  const [password, setPassword] = React.useState('')
  const [remember, setRemember] = React.useState(true)
  const [error, setError] = React.useState<string | null>(null)
  const [loading, setLoading] = React.useState(false)

  const search = router.state.location.search as { next?: string } | undefined
  const next = React.useMemo(() => normalizeNextPath(search?.next) ?? undefined, [search?.next])

  React.useEffect(() => {
    if (!user) return
    navigate({ to: '/', search: next ? { next } : undefined, replace: true })
  }, [navigate, next, user])

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    setLoading(true)
    try {
      await login(username, password, remember)
    } catch (err: any) {
      setError(getLoginErrorMessage(err))
      setLoading(false)
    }
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'grid',
        placeItems: { xs: 'start center', sm: 'center' },
        px: 2,
        py: { xs: 3, sm: 4 },
        bgcolor: 'background.default',
      }}
    >
      <Paper variant="outlined" sx={{ p: { xs: 2.5, sm: 4 }, width: 440, maxWidth: '100%' }}>
        <Stack spacing={1} sx={{ mb: 3 }}>
          <PortalBrand center stacked />
          <Typography variant="h6" component="h1" textAlign="center" sx={{ fontWeight: 700 }}>
            Logg inn
          </Typography>
        </Stack>

        {next && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Logg inn for å fortsette.
          </Alert>
        )}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <form onSubmit={onSubmit} aria-busy={loading}>
          <Stack spacing={2}>
            <TextField
              label="E-post"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoComplete="username"
              autoFocus
              disabled={loading}
              required
              fullWidth
            />
            <TextField
              label="Passord"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              disabled={loading}
              required
              fullWidth
            />
            <FormControlLabel
              control={<Checkbox checked={remember} disabled={loading} onChange={(e) => setRemember(e.target.checked)} />}
              label="Husk meg"
            />
            <Button
              type="submit"
              variant="contained"
              disabled={loading}
              fullWidth
              startIcon={loading ? <CircularProgress color="inherit" size={18} /> : undefined}
              sx={{ minHeight: 44 }}
            >
              {loading ? 'Logger inn...' : 'Logg inn'}
            </Button>
            <Button href="/admin/reset/request" variant="text" disabled={loading} fullWidth>
              Glemt passord?
            </Button>
          </Stack>
        </form>
      </Paper>
    </Box>
  )
}
